import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getAdminToken, setAdminToken, removeAdminToken } from './auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useAdminAuth = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    const token = getAdminToken();
    if (token) setIsLoggedIn(true);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/login`, { password });
      if (res.data.success) {
        setAdminToken(res.data.token);
        setIsLoggedIn(true);
        toast.success('Logged in successfully');
      }
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    removeAdminToken();
    setIsLoggedIn(false);
  };

  return { isLoggedIn, password, setPassword, handleLogin, authLoading, handleLogout };
};

export const useBlogEditor = (slug, navigate, handleLogout) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: 'SuperTags Team',
    read_time: '3 min read',
    tags: '',
    category: 'General',
    seo_title: '',
    meta_description: '',
    featured_image: ''
  });

  const fetchBlogToEdit = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/blogs/${slug}`);
      setFormData({
        title: res.data.title,
        excerpt: res.data.excerpt,
        content: res.data.content,
        author: res.data.author,
        read_time: res.data.read_time,
        tags: res.data.tags ? res.data.tags.join(', ') : '',
        category: res.data.category || 'General',
        seo_title: res.data.seo_title || '',
        meta_description: res.data.meta_description || '',
        featured_image: res.data.featured_image || ''
      });
    } catch (err) {
      toast.error('Failed to load blog for editing');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (slug) {
      fetchBlogToEdit();
    }
  }, [slug, fetchBlogToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const payload = {
      ...formData,
      tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
    };

    const token = getAdminToken();
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (slug) {
        await axios.put(`${BACKEND_URL}/api/blogs/${slug}`, payload, { headers });
        toast.success('Blog post updated successfully!');
        navigate(`/blog/${slug}`);
      } else {
        const response = await axios.post(`${BACKEND_URL}/api/blogs`, payload, { headers });
        toast.success('Blog post published successfully!');
        navigate(`/blog/${response.data.slug}`);
      }
    } catch (error) {
      toast.error(slug ? 'Failed to update blog post.' : 'Failed to publish blog post.');
      if (error?.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  return { formData, loading, handleChange, handleSubmit };
};
